import { faChevronDown, faSpinnerThird, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence } from "motion/react";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { mergeProps, useButton, useFilter, useFocusRing, useHover, usePress } from "react-aria";
import { FormProvider } from "react-hook-form";
import { tv } from "tailwind-variants";

import { useDomRef } from "../../hooks/useDomRef";
import { cn } from "../../utils/cn";
import { filterDOMProps } from "../../utils/filterDomProps";
import { formValueFromSelectedKeys, selectedKeysFromFormValue } from "../../utils/misc";
import { mergeRefs } from "../../utils/refs";
import { formLabel } from "../../utils/tailwindClasses";
import { ScrollShadow } from "../extra/ScrollShadow";
import { useFormItemContext } from "../form/FormItem";
import { useControllerSafe } from "../form/useControllerSafe";
import { Input } from "../input/Input";
import { ListBox } from "../listbox/ListBox";
import StandalonePopover from "../popover/StandalonePopover";
import { MultiSelectTags } from "../tag/MultiSelectTags";
import { HiddenSelect } from "./HiddenSelect";
import { useMultiSelect } from "./useMultiSelect";
import { useMultiSelectState } from "./useMultiSelectState";

import type { CollectionChildren, Key } from "@react-types/shared";
import type { ComponentPropsWithoutRef, ForwardedRef, ReactElement, ReactNode, Ref } from "react";
import type { VariantProps } from "tailwind-variants";
import type { ReactRef, SlotsToClasses } from "../../utils/types";
import type { ScrollShadowProps } from "../extra/ScrollShadow";
import type { ListBoxProps } from "../listbox/ListBox";
import type { UsePopoverProps } from "../popover/usePopover";
import type { RenderTagProps } from "../tag/MultiSelectTags";
import type { AriaHiddenSelectProps, HiddenSelectProps } from "./HiddenSelect";
import type { MultiSelectProps } from "./useMultiSelectState";

// Select renders an Input internally for search but we don't want it to hook into the form context
// since we're already managing the form state ourselves. We block the inner input from seeing
// the actual form context by inserting a blank one in between
const FormContextBarrier = (props: { children: ReactNode }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <FormProvider {...(props as any)} />;
};

// eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values
const selectVariants = tv({
  slots: {
    base: "group relative flex flex-col",
    label: [...formLabel],
    mainWrapper: "flex w-full flex-col",
    trigger: [
      "cursor-pointer",
      "relative",
      "px-3",
      "gap-3",
      "w-full",
      "inline-flex",
      "flex-row",
      "items-center",
      "shadow-sm",
      "outline-none",
      "tap-highlight-transparent",
      "rounded",
      "bg-white",
      "dark:bg-gray-800",
      "border",
      "border-solid",
      "border-gray-150",
      "dark:border-gray-700",
      "data-[hover=true]:border-gray-300",
      "dark:data-[hover=true]:border-gray-600",
      "data-[open=true]:border-blue-400",
      "data-[focus-visible=true]:border-blue-400",
      "dark:data-[open=true]:border-blue-700",
      "dark:data-[focus-visible=true]:border-blue-700",
    ],
    innerWrapper:
      "box-border inline-flex h-full min-h-4 w-[calc(100%_-_theme(spacing.10))] items-center gap-1.5",
    selectorIcon:
      "absolute right-3 size-4 text-xs text-gray-500 hover:text-gray-900 dark:hover:text-gray-300",
    spinner:
      "absolute right-3 text-xs text-gray-300 [--fa-animation-duration:0.5s] dark:text-gray-600",
    value: [
      "w-full",
      "text-left",
      "font-normal",
      "text-gray-500",
      "dark:text-gray-300",
      "group-data-[has-value=true]:text-gray-900",
      "dark:group-data-[has-value=true]:text-white",
    ],
    placeholder: "absolute top-1/2 inline-block -translate-y-1/2 px-0.5 text-gray-500",
    listboxWrapper: "max-h-64 w-full scroll-py-6",
    listbox: "",
    popoverContent: "w-full overflow-hidden p-1",
    helperWrapper: "relative flex flex-col gap-1.5 p-1 group-data-[has-helper=true]:flex",
    description: "text-xs text-gray-400",
    errorMessage: "text-xs text-red",
    clearButton: [
      "p-2",
      "absolute",
      "right-8",
      "appearance-none",
      "outline-none",
      "cursor-pointer",
      "hidden",
      "group-data-[has-value=true]:block",
      "text-gray-500",
      "hover:text-gray-900",
      "dark:hover:text-gray-300",
      "transition",
    ],
    searchWrapper: "w-full px-2 pb-1 pt-2",
  },
  variants: {
    size: {
      sm: {
        trigger: "h-7 min-h-7 rounded px-2",
        value: "text-sm",
        placeholder: "text-sm",
        clearButton: "text-xs",
      },
      md: {
        trigger: "h-8 min-h-8 rounded",
        value: "text-sm",
        placeholder: "text-sm",
        clearButton: "text-sm",
      },
      lg: {
        trigger: "h-10 min-h-10 rounded-md",
        value: "text-base",
        placeholder: "text-base",
        clearButton: "text-sm",
      },
    },
    labelPlacement: {
      top: {
        base: "flex flex-col",
        label: [
          "pe-2",
          "max-w-full",
          "text-ellipsis",
          "overflow-hidden",
          "group-data-[filled=true]:text-gray-900",
          "dark:group-data-[filled=true]:text-gray-100",
          "group-data-[filled=true]:pointer-events-auto",
        ],
      },
      left: {
        base: "flex-row flex-nowrap items-center data-[has-helper=true]:items-start",
        label: "relative pr-2 text-gray-900 dark:text-gray-100",
      },
    },
    fullWidth: {
      true: {
        base: "w-full",
      },
      false: {
        mainWrapper: "min-w-64",
      },
    },
    isDisabled: {
      true: {
        base: "pointer-events-none opacity-60",
        trigger: "pointer-events-none",
      },
    },
    isInvalid: {
      true: {
        label: "!text-red",
        value: "!text-red",
        selectorIcon: "text-red",
        trigger: "!border-red group-data-[focus=true]:border-red",
      },
    },
    isRequired: {
      true: {
        label: "after:ml-0.5 after:text-red after:content-['*']",
      },
    },
    isMultiline: {
      true: {
        label: "relative",
        trigger: "!h-auto",
      },
      false: {
        value: "truncate",
      },
    },
    disableAnimation: {
      true: {
        trigger: "after:transition-none",
        base: "transition-none",
        label: "transition-none",
        selectorIcon: "transition-none",
      },
      false: {
        base: "transition-background !duration-150 motion-reduce:transition-none",
        label: [
          "will-change-auto",
          "origin-top-left",
          "!duration-200",
          "!ease-out",
          "motion-reduce:transition-none",
          "transition-[transform,color,left,opacity]",
        ],
        selectorIcon: "ease transition-transform duration-150 motion-reduce:transition-none",
        trigger: "transition-all !duration-150 motion-reduce:transition-none",
      },
    },
    disableSelectorIconRotation: {
      true: {},
      false: {
        selectorIcon: "data-[open=true]:rotate-180",
      },
    },
    hasOnlyErrorMessage: {
      true: {
        // Don't want error message to take up extra space since it will appear and disappear,
        // causing the layout to shift. Description is generally either always there or never there
        // so it's fine to take up space.
        helperWrapper: "absolute bottom-0 translate-y-full",
      },
    },
  },
  compoundVariants: [
    {
      labelPlacement: "top",
      isMultiline: false,
      class: {
        base: "group relative justify-end",
        label: ["pb-0", "z-20", "top-1/2", "-translate-y-1/2", "group-data-[filled=true]:left-0"],
      },
    },
    // top & size
    {
      labelPlacement: "top",
      size: "sm",
      isMultiline: false,
      class: {
        label: [
          "left-2",
          "group-data-[filled=true]:-translate-y-[calc(100%_+_theme(fontSize.xs)/2_+_16px)]",
        ],
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_8px)]",
      },
    },
    {
      labelPlacement: "top",
      isMultiline: false,
      size: "md",
      class: {
        label: [
          "left-3",
          "group-data-[filled=true]:-translate-y-[calc(100%_+_theme(fontSize.xs)/2_+_16px)]",
        ],
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_10px)]",
      },
    },
    {
      labelPlacement: "top",
      isMultiline: false,
      size: "lg",
      class: {
        label: [
          "left-3",
          "text-sm",
          "group-data-[filled=true]:-translate-y-[calc(100%_+_theme(fontSize.sm)/2_+_24px)]",
        ],
        base: "data-[has-label=true]:mt-[calc(theme(fontSize.sm)_+_12px)]",
      },
    },
    {
      labelPlacement: "left",
      size: "sm",
      class: {
        label: "group-data-[has-helper=true]:pt-2",
      },
    },
    {
      labelPlacement: "left",
      size: "md",
      class: {
        label: "group-data-[has-helper=true]:pt-3",
      },
    },
    {
      labelPlacement: "left",
      size: "lg",
      class: {
        label: "group-data-[has-helper=true]:pt-4",
      },
    },
    // isMultiline & labelPlacement="top"
    {
      labelPlacement: "top",
      isMultiline: true,
      class: {
        label: "pb-1.5",
      },
    },
  ],
  defaultVariants: {
    size: "md",
    labelPlacement: "top",
    fullWidth: false,
    isDisabled: false,
    isMultiline: false,
    disableAnimation: false,
    disableSelectorIconRotation: false,
  },
});

export type SelectVariantProps = VariantProps<typeof selectVariants>;
export type SelectSlots = keyof ReturnType<typeof selectVariants>;

export type SelectedItemProps<T = object> = {
  /** A unique key for the item. */
  key?: Key;
  /** The props passed to the item. */
  props?: Record<string, any>;
  /** The item data. */
  data?: T | null;
  /** An accessibility label for this item. */
  "aria-label"?: string;
  /** The rendered contents of this item (e.g. JSX). */
  rendered?: ReactNode;
  /** A string value for this item, used for features like typeahead. */
  textValue?: string;
  /** The type of item this item represents. */
  type?: string;
};

export type SelectedItems<T = object> = Array<SelectedItemProps<T>>;

interface Props<T> extends Omit<ComponentPropsWithoutRef<"select">, keyof SelectVariantProps> {
  /**
   * The ref to the scroll element. Useful when having async loading of items.
   */
  scrollRef?: ReactRef<HTMLElement | null>;
  /**
   * Whether the select is required.
   * @default false
   */
  isRequired?: boolean;
  /**
   * Whether to show the clear button.
   */
  isClearable?: boolean;
  /**
   * Element to be rendered in the left side of the select.
   */
  startContent?: React.ReactNode;
  /**
   * Element to be rendered in the right side of the select.
   */
  endContent?: ReactNode;
  /**
   * The placeholder for the select to display when no option is selected.
   * @default "Select an option"
   */
  placeholder?: string;
  /**
   * Whether to display a top and bottom arrow indicators when the listbox is scrollable.
   * @default true
   */
  showScrollIndicators?: boolean;
  /**
   * Props to be passed to the popover component.
   *
   * @default { placement: "bottom", triggerScaleOnOpen: false, offset: 5 }
   */
  popoverProps?: Partial<UsePopoverProps>;
  /**
   * Props to be passed to the listbox component.
   *
   * @default { disableAnimation: false }
   */
  listboxProps?: Partial<ListBoxProps<T>>;
  /**
   * Props to be passed to the scroll shadow component. This component
   * adds a shadow to the top and bottom of the listbox when it is scrollable.
   *
   * @default { hideScrollBar: true, offset: 15 }
   */
  scrollShadowProps?: Partial<ScrollShadowProps>;
  /**
   * Function to render the value of the select. It renders the selected item by default.
   * @param value
   * @returns
   */
  renderValue?: (items: SelectedItems<T>) => ReactNode;
  /**
   * Callback fired when the select menu is closed.
   */
  onClose?: () => void;
  /**
   * Callback fired when the value is cleared.
   * if you pass this prop, the clear button will be shown.
   */
  onClear?: () => void;
  /**
   * Classes object to style the select and its children.
   */
  classNames?: SlotsToClasses<SelectSlots>;

  /**
   * Custom icon to show on the right side of the select.
   */
  selectorIcon?: ReactNode;

  /**
   * Whether to show a search input in the dropdown.
   * @default false
   */
  showSearch?: boolean;

  /**
   * Custom filter function for search. If set to false, client-side filtering is disabled
   * (useful for server-side search). The function receives the search input and option data.
   * @param inputValue - The current search input value
   * @param option - The option data object (contains textValue, key, etc.)
   * @returns Whether the option should be shown
   */
  filterOption?:
    | false
    | ((inputValue: string, option: { textValue?: string; key?: Key }) => boolean);

  /**
   * Callback fired when the search input value changes.
   * Use this for server-side search when filterOption is false.
   */
  onSearch?: (value: string) => void;

  /**
   * Placeholder text for the search input.
   * @default "Search..."
   */
  searchPlaceholder?: string;

  /**
   * Custom function to render each tag in multi-select mode.
   * Useful for styling tags (e.g., with custom colors).
   */
  renderTag?: (props: RenderTagProps) => ReactNode;

  /**
   * Content to render at the bottom of the dropdown popover.
   * Useful for adding "Create new..." links or other actions.
   */
  popoverFooter?: ReactNode;
}

export type SelectProps<T> = Omit<Props<T>, keyof MultiSelectProps<T>> &
  AriaHiddenSelectProps &
  MultiSelectProps<T> &
  SelectVariantProps;

function SelectImpl<T extends object>(props: SelectProps<T>, ref: ForwardedRef<HTMLSelectElement>) {
  const {
    isOpen,
    label,
    name,
    isLoading,
    defaultOpen,
    onOpenChange,
    startContent,
    endContent,
    description,
    errorMessage: errorMessageProp,
    renderValue,
    onSelectionChange,
    placeholder,
    children,
    disallowEmptySelection = false,
    selectionMode = "single",
    scrollRef: scrollRefProp,
    popoverProps: popoverPropsProp = {},
    scrollShadowProps = {},
    listboxProps: listboxPropsProp = {},
    validationState,
    onChange,
    onClose,
    onClear,
    className,
    classNames,
    size,
    labelPlacement: labelPlacementProp,
    fullWidth,
    isDisabled: isDisabledProp,
    isInvalid: isInvalidProp,
    isMultiline,
    disableAnimation = false,
    disableSelectorIconRotation,
    selectorIcon,
    showSearch = false,
    filterOption,
    onSearch,
    searchPlaceholder = "Search...",
    renderTag,
    popoverFooter,
    ...otherProps
  } = props;

  const formController = useControllerSafe({ name: name ?? "" });

  if (formController && !name) {
    throw new Error("Select used inside a Form component must have a name prop");
  }

  // Check if we're inside a Form.Item - if so, skip our own error display and default to fullWidth
  const isInsideFormItem = useFormItemContext();

  // When inside a Form.Item, default to fullWidth unless explicitly set to false
  const effectiveFullWidth = isInsideFormItem && fullWidth !== false ? true : fullWidth;

  const formError = formController ? formController.fieldState.error : undefined;

  // Don't show error message when inside Form.Item - let Form.Item handle error display
  const errorMessage = isInsideFormItem
    ? undefined
    : formError
      ? String(formError.message)
      : typeof errorMessageProp === "function"
        ? null
        : errorMessageProp;
  const isDisabled = formController ? formController.field.disabled : isDisabledProp;

  const scrollShadowRef = useDomRef(scrollRefProp);

  const slotsProps: {
    popoverProps: SelectProps<T>["popoverProps"];
    scrollShadowProps: SelectProps<T>["scrollShadowProps"];
    listboxProps: SelectProps<T>["listboxProps"];
  } = {
    popoverProps: mergeProps(
      {
        placement: "bottom",
        triggerScaleOnOpen: false,
        offset: 5,
        disableAnimation,
      },
      popoverPropsProp
    ),
    scrollShadowProps: mergeProps(
      {
        ref: scrollShadowRef,
        isEnabled: props.showScrollIndicators ?? true,
        hideScrollBar: true,
        offset: 15,
      },
      scrollShadowProps
    ),
    listboxProps: mergeProps(
      {
        disableAnimation,
      },
      listboxPropsProp
    ),
  };

  const domRef = useDomRef(ref);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search state
  const [searchValue, setSearchValue] = useState("");
  const { contains } = useFilter({ sensitivity: "base" });

  const state = useMultiSelectState<T>({
    ...props,
    isOpen,
    selectionMode,
    disallowEmptySelection,
    children: children as CollectionChildren<T>,
    isRequired: props.isRequired,
    isDisabled,
    defaultOpen,
    selectedKeys: formController
      ? selectedKeysFromFormValue(formController.field.value, selectionMode)
      : props.selectedKeys,
    onOpenChange: open => {
      onOpenChange?.(open);
      if (!open) {
        onClose?.();
        if (formController) {
          formController.field.onBlur();
        }
      }
    },
    onSelectionChange: keys => {
      onSelectionChange?.(keys);
      if (onChange && typeof onChange === "function" && domRef.current) {
        const event = {
          target: {
            ...domRef.current,
            value: Array.from(keys).join(","),
            name: domRef.current.name,
          },
        } as React.ChangeEvent<HTMLSelectElement>;

        onChange(event);
      }
      if (formController) {
        formController.field.onChange(formValueFromSelectedKeys(keys, selectionMode));
      }
    },
  });

  const {
    labelProps: ariaLabelProps,
    triggerProps: ariaTriggerProps,
    valueProps: ariaValueProps,
    menuProps,
    descriptionProps: ariaDescriptionProps,
    errorMessageProps: ariaErrorMessageProps,
  } = useMultiSelect(
    { ...props, disallowEmptySelection, isDisabled },
    state,
    triggerRef as React.RefObject<HTMLElement>
  );

  const { isPressed, buttonProps } = useButton(ariaTriggerProps, triggerRef);

  const { focusProps, isFocused, isFocusVisible } = useFocusRing();
  const { isHovered, hoverProps } = useHover({ isDisabled });

  const { focusProps: clearFocusProps, isFocusVisible: isClearButtonFocusVisible } = useFocusRing();

  const { pressProps: clearPressProps } = usePress({
    isDisabled: !!isDisabled,
    onPress: () => {
      state.setSelectedKeys(new Set());
      onClear?.();
    },
  });

  const isClearable = !!onClear || props.isClearable;

  // Clear search when dropdown closes and focus search input when it opens
  useEffect(() => {
    if (!state.isOpen) {
      setSearchValue("");
    } else if (showSearch && searchInputRef.current) {
      // Small delay to ensure popover is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [state.isOpen, showSearch]);

  // Compute hidden keys based on search value (for client-side filtering)
  const hiddenKeys = useMemo(() => {
    // No filtering needed if showSearch is disabled, no search value, or server-side filtering
    if (!showSearch || !searchValue || filterOption === false) {
      return undefined;
    }

    const hidden = new Set<Key>();
    for (const item of state.collection) {
      if (item.type === "section") {
        // Handle sections - hide child items that don't match
        for (const child of item.childNodes) {
          const shouldShow = filterOption
            ? filterOption(searchValue, { textValue: child.textValue, key: child.key })
            : contains(child.textValue || "", searchValue);
          if (!shouldShow) {
            hidden.add(child.key);
          }
        }
      } else {
        const shouldShow = filterOption
          ? filterOption(searchValue, { textValue: item.textValue, key: item.key })
          : contains(item.textValue || "", searchValue);
        if (!shouldShow) {
          hidden.add(item.key);
        }
      }
    }
    return hidden.size > 0 ? hidden : undefined;
  }, [showSearch, searchValue, filterOption, state.collection, contains]);

  const labelPlacement = useMemo<SelectVariantProps["labelPlacement"]>(() => {
    if (!labelPlacementProp && !label) {
      return "top";
    }

    return labelPlacementProp ?? "top";
  }, [labelPlacementProp, label]);

  const hasHelper = !!description || !!errorMessage;
  const hasOnlyErrorMessage = !!errorMessage && !description;
  const hasPlaceholder = !!placeholder;
  const isInvalid = formError ? true : validationState === "invalid" || isInvalidProp;

  const isFilled =
    state.isOpen ||
    hasPlaceholder ||
    !!state.selectedItems ||
    !!startContent ||
    !!endContent ||
    !!isMultiline;
  const hasValue = !!state.selectedItems;
  const hasLabel = !!label;

  const slots = useMemo(
    () =>
      selectVariants({
        size,
        fullWidth: effectiveFullWidth,
        isDisabled,
        isMultiline,
        disableAnimation,
        isInvalid,
        labelPlacement,
        disableSelectorIconRotation,
        hasOnlyErrorMessage,
        className,
      }),
    [
      size,
      effectiveFullWidth,
      isDisabled,
      isMultiline,
      disableAnimation,
      isInvalid,
      labelPlacement,
      disableSelectorIconRotation,
      hasOnlyErrorMessage,
      className,
    ]
  );

  // scroll the listbox to the selected item
  useEffect(() => {
    if (state.isOpen && popoverRef.current && listBoxRef.current) {
      const selectedItem = listBoxRef.current.querySelector(
        "[aria-selected=true] [data-label=true]"
      );
      const scrollShadow = scrollShadowRef.current;

      // scroll the listbox to the selected item
      if (selectedItem && scrollShadow && selectedItem.parentElement) {
        const scrollShadowRect = scrollShadow?.getBoundingClientRect();
        const scrollShadowHeight = scrollShadowRect.height;

        scrollShadow.scrollTop =
          selectedItem.parentElement.offsetTop -
          scrollShadowHeight / 2 +
          selectedItem.parentElement.clientHeight / 2;
      }
    }
  }, [state.isOpen, disableAnimation]);

  // apply the same with to the popover as the select
  useEffect(() => {
    if (state.isOpen && popoverRef.current && triggerRef.current) {
      const selectRect = triggerRef.current.getBoundingClientRect();
      const popover = popoverRef.current;

      popover.style.width = selectRect.width + "px";
    }
  }, [state.isOpen]);

  const baseClassName = cn(classNames?.base, className);

  const baseProps = useMemo(
    () => ({
      "data-slot": "base",
      "data-filled": isFilled,
      "data-has-value": hasValue,
      "data-has-label": hasLabel,
      "data-has-helper": hasHelper,
      className: slots.base({ className: baseClassName }),
    }),
    [slots, hasHelper, hasValue, hasLabel, isFilled, baseClassName]
  );

  const triggerProps = useMemo(() => {
    return {
      ref: triggerRef,
      "data-slot": "trigger",
      "data-open": state.isOpen,
      "data-disabled": isDisabled,
      "data-focus": isFocused,
      "data-pressed": isPressed,
      "data-focus-visible": isFocusVisible,
      "data-hover": isHovered,
      className: slots.trigger({
        className: cn(
          classNames?.trigger,
          state.selectionMode === "multiple" && !!state.selectedItems?.length && "pl-1"
        ),
      }),
      ...mergeProps(buttonProps, focusProps, hoverProps, filterDOMProps(otherProps)),
    };
  }, [
    slots,
    triggerRef,
    state.isOpen,
    state.selectionMode,
    state.selectedItems,
    classNames?.trigger,
    isDisabled,
    isFocused,
    isPressed,
    isFocusVisible,
    isHovered,
    buttonProps,
    focusProps,
    hoverProps,
    otherProps,
  ]);

  const hiddenSelectProps = useMemo(
    () =>
      ({
        state,
        triggerRef,
        selectRef: formController ? mergeRefs(domRef, formController.field.ref) : domRef,
        selectionMode,
        label: props?.label,
        name: props?.name,
        isRequired: props?.isRequired,
        autoComplete: props?.autoComplete,
        isDisabled,
        onChange,
        value: formController?.field.value,
      }) as HiddenSelectProps<T>,
    [
      state,
      selectionMode,
      props?.label,
      props?.autoComplete,
      props?.name,
      isDisabled,
      triggerRef,
      formController,
    ]
  );

  const labelProps = useMemo(
    () => ({
      "data-slot": "label",
      className: slots.label({
        className: classNames?.label,
      }),
      ...ariaLabelProps,
    }),
    [slots, classNames?.label, ariaLabelProps]
  );

  const valueProps = useMemo(
    () => ({
      "data-slot": "value",
      className: slots.value({
        className: classNames?.value,
      }),
      ...ariaValueProps,
    }),
    [slots, classNames?.value, ariaValueProps]
  );

  const listboxWrapperProps = useMemo(
    () => ({
      "data-slot": "listbox-wrapper",
      className: slots.listboxWrapper({
        class: classNames?.listboxWrapper,
      }),
      ...slotsProps.scrollShadowProps,
    }),
    [slots.listboxWrapper, classNames?.listboxWrapper, slotsProps.scrollShadowProps]
  );

  const listboxProps = useMemo(
    () =>
      ({
        state,
        ref: listBoxRef,
        "data-slot": "listbox",

        className: slots.listbox({
          className: classNames?.listbox,
        }),

        ...mergeProps(slotsProps.listboxProps, menuProps),
      }) as ListBoxProps<T>,
    [
      listBoxRef,
      slots.listbox,
      classNames?.listbox,
      props?.className,
      slotsProps.listboxProps,
      menuProps,
    ]
  );

  const popoverProps = useMemo(
    () => ({
      state,
      "data-slot": "popover",
      scrollRef: listBoxRef as React.RefObject<HTMLElement>,
      triggerType: "listbox" as UsePopoverProps["triggerType"],
      classNames: {
        content: slots.popoverContent({
          class: classNames?.popoverContent,
        }),
      },
      ...slotsProps.popoverProps,
      ref: popoverRef,
      offset:
        state.selectedItems && state.selectedItems.length > 0
          ? // forces the popover to update its position when the selected items change
            state.selectedItems.length * 0.00000001 + (slotsProps.popoverProps?.offset || 0)
          : slotsProps.popoverProps?.offset,
    }),
    [
      slots.popoverContent,
      classNames?.popoverContent,
      slotsProps.popoverProps,
      triggerRef,
      state,
      state.selectedItems,
    ]
  );

  const innerWrapperProps = useMemo(
    () => ({
      "data-slot": "inner-wrapper",
      className: slots.innerWrapper({
        className: classNames?.innerWrapper,
      }),
    }),
    [slots.innerWrapper, classNames?.innerWrapper]
  );

  const helperWrapperProps = useMemo(
    () => ({
      "data-slot": "helper-wrapper",
      className: slots.helperWrapper({
        className: classNames?.helperWrapper,
      }),
    }),
    [slots.helperWrapper, classNames?.helperWrapper]
  );

  const descriptionProps = useMemo(
    () => ({
      ...ariaDescriptionProps,
      "data-slot": "description",
      className: slots.description({ className: classNames?.description }),
    }),
    [slots.description, classNames?.description]
  );

  const clearButtonProps = useMemo(
    () => ({
      role: "button",
      tabIndex: 0,
      "data-slot": "clear-button",
      "data-focus-visible": isClearButtonFocusVisible,
      ...mergeProps(clearPressProps, clearFocusProps),
      className: slots.clearButton(),
    }),
    [isClearButtonFocusVisible, clearPressProps, clearFocusProps, slots.clearButton]
  );

  const mainWrapperProps = useMemo(() => {
    return {
      "data-slot": "main-wrapper",
      className: slots.mainWrapper({
        className: classNames?.mainWrapper,
      }),
    };
  }, [slots.mainWrapper, classNames?.mainWrapper]);

  const errorMessageProps = useMemo(
    () => ({
      ...ariaErrorMessageProps,
      "data-slot": "error-message",
      className: slots.errorMessage({ className: classNames?.errorMessage }),
    }),
    [slots.errorMessage, ariaErrorMessageProps, classNames?.errorMessage]
  );

  const selectorIconProps = useMemo(
    () => ({
      "data-slot": "selector-icon",
      "aria-hidden": true,
      "data-open": state.isOpen,
      className: slots.selectorIcon({ class: classNames?.selectorIcon }),
    }),
    [slots.selectorIcon, classNames?.selectorIcon, state.isOpen]
  );

  const spinnerProps = useMemo(
    () => ({
      "data-slot": "spinner",
      "aria-hidden": true,
      className: slots.spinner({ className: classNames?.spinner }),
    }),
    [slots.spinner, classNames?.spinner]
  );

  const helperWrapper = useMemo(() => {
    if (!hasHelper) return null;

    return (
      <div {...helperWrapperProps}>
        {errorMessage ? (
          <div {...errorMessageProps}>{errorMessage}</div>
        ) : description ? (
          <div {...descriptionProps}>{description}</div>
        ) : null}
      </div>
    );
  }, [
    hasHelper,
    errorMessage,
    description,
    helperWrapperProps,
    errorMessageProps,
    descriptionProps,
  ]);

  // There is a render cycle in between when an item is added and when the tag shows up in the
  // select - we track this explicitly based on the visibility of the tags to avoid the
  // placeholder flickering when the first tag is added
  const [multiSelectTagsVisible, setMultiSelectTagsVisible] = useState(false);

  const showPlaceholder =
    selectionMode === "multiple" ? !multiSelectTagsVisible : state.selectedKeys.size === 0;

  const selectedItems = useMemo(() => {
    const selectedItems = state.selectedItems ?? [];

    if (renderValue && typeof renderValue === "function") {
      const mappedItems = selectedItems.map(item => ({
        key: item.key,
        data: item.value,
        type: item.type,
        props: item.props,
        textValue: item.textValue,
        rendered: item.rendered,
        "aria-label": item["aria-label"],
      }));

      return <span {...valueProps}>{renderValue(mappedItems)}</span>;
    }

    if (state.selectionMode === "multiple") {
      return (
        <MultiSelectTags
          state={state}
          {...valueProps}
          onVisibleChange={count => setMultiSelectTagsVisible(count > 0)}
          renderTag={renderTag}
        />
      );
    }

    if (selectedItems.length > 0) {
      return <span {...valueProps}>{selectedItems.map(item => item.textValue).join(", ")}</span>;
    }
  }, [state, renderValue, valueProps, renderTag]);

  const renderIndicator = useMemo(() => {
    if (isLoading) {
      return (
        <span {...spinnerProps}>
          <FontAwesomeIcon icon={faSpinnerThird} spin />
        </span>
      );
    }

    return (
      <span {...selectorIconProps}>{selectorIcon || <FontAwesomeIcon icon={faChevronDown} />}</span>
    );
  }, [isLoading, spinnerProps, selectorIcon, selectorIconProps]);

  const popoverContent = useMemo(
    () =>
      state.isOpen ? (
        <StandalonePopover
          {...popoverProps}
          state={state}
          triggerRef={triggerRef as React.RefObject<HTMLElement>}
        >
          {showSearch && (
            <div className={slots.searchWrapper()}>
              <FormContextBarrier>
                <Input
                  ref={searchInputRef}
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onValueChange={value => {
                    setSearchValue(value);
                    onSearch?.(value);
                  }}
                  isSearch
                  size="sm"
                  fullWidth
                  // Prevent the popover from closing when clicking the input
                  onMouseDown={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                />
              </FormContextBarrier>
            </div>
          )}
          <ScrollShadow {...listboxWrapperProps}>
            <ListBox {...listboxProps} hiddenKeys={hiddenKeys} />
          </ScrollShadow>
          {popoverFooter}
        </StandalonePopover>
      ) : null,
    [
      popoverProps,
      state,
      triggerRef,
      listboxWrapperProps,
      listboxProps,
      showSearch,
      slots,
      searchPlaceholder,
      searchValue,
      setSearchValue,
      onSearch,
      hiddenKeys,
      popoverFooter,
    ]
  );

  const end = useMemo(() => {
    if (isClearable) {
      return <span {...clearButtonProps}>{endContent || <FontAwesomeIcon icon={faXmark} />}</span>;
    }

    return endContent;
  }, [isClearable, clearButtonProps, endContent]);

  const labelContent = useMemo(
    () => (label ? <label {...labelProps}>{label}</label> : null),
    [label, labelProps]
  );

  return (
    <div {...baseProps}>
      {labelPlacement === "left" ? labelContent : null}
      <HiddenSelect {...hiddenSelectProps} />
      <div {...mainWrapperProps}>
        <button {...triggerProps}>
          {labelPlacement !== "left" ? labelContent : null}
          <div {...innerWrapperProps}>
            {startContent}
            {showPlaceholder && <span className={slots.placeholder()}>{placeholder}</span>}
            {selectedItems}
            {end}
          </div>
          {renderIndicator}
        </button>
        {helperWrapper}
      </div>
      {disableAnimation ? popoverContent : <AnimatePresence>{popoverContent}</AnimatePresence>}
    </div>
  );
}

/**
 * A collapsible list of options.
 */
const WrappedSelect = forwardRef(SelectImpl) as <T = object>(
  props: SelectProps<T> & { ref?: Ref<HTMLElement> }
) => ReactElement<unknown>;

export const Select = WrappedSelect as typeof WrappedSelect & {
  displayName: string;
  Item: typeof ListBox.Item;
  Section: typeof ListBox.Section;
};

Select.displayName = "Select";
Select.Item = ListBox.Item;
Select.Section = ListBox.Section;

export type { ListBoxItemProps as SelectItemProps } from "../listbox/ListBoxItem";
export type { ListBoxSectionProps as SelectSectionProps } from "../listbox/ListBoxSection";
